import { useCallback, useState } from 'react'
import { erc20Abi, parseUnits } from 'viem'
import { useAccount, useConnect, useSendTransaction, useSwitchChain, useWriteContract } from 'wagmi'
import { getChainForNetwork } from '@/lib/payment-chains'
import { showApiErrorToast } from '@/lib/api-toast'
import { useDepositMetaMaskMutation } from '@/store/api/paymentApi'
import type { Currency } from '@/types/payment'

interface UseMetaMaskDepositOptions {
	currency: Currency | null
	amount: string
	adminAddress: string | null
}

export function useMetaMaskDeposit({ currency, amount, adminAddress }: UseMetaMaskDepositOptions) {
	const { address, chainId, isConnected } = useAccount()
	const { connectAsync, connectors, isPending: isConnecting } = useConnect()
	const { switchChainAsync, isPending: isSwitchingChain } = useSwitchChain()
	const { sendTransactionAsync, isPending: isSendingNative } = useSendTransaction()
	const { writeContractAsync, isPending: isSendingToken } = useWriteContract()
	const [depositMetaMask, { isLoading: isRecordingDeposit }] = useDepositMetaMaskMutation()
	const [lastError, setLastError] = useState<string | null>(null)

	const targetChain = currency ? getChainForNetwork(currency.network) : undefined
	const isWrongChain = Boolean(isConnected && targetChain && chainId !== targetChain.id)
	const isSubmitting = isConnecting || isSwitchingChain || isSendingNative || isSendingToken || isRecordingDeposit
	const nativeSymbol = targetChain?.nativeCurrency?.symbol?.toUpperCase()
	const currencyCode = currency?.currency_code?.toUpperCase()
	const isNativeTransfer = Boolean(nativeSymbol && currencyCode && nativeSymbol === currencyCode)

	const connectWallet = useCallback(async () => {
		const connector = connectors.find((item) => item.id === 'metaMask' || item.name.toLowerCase().includes('metamask')) ?? connectors[0]

		if (!connector) {
			throw new Error('MetaMask connector is not available.')
		}

		await connectAsync({ connector })
	}, [connectAsync, connectors])

	const ensureWalletReady = useCallback(async () => {
		if (!currency || !adminAddress) {
			throw new Error('Deposit details are incomplete.')
		}

		if (!targetChain) {
			throw new Error(`Unsupported network: ${currency.network}`)
		}

		if (!isConnected) {
			await connectWallet()
		}

		if (chainId !== targetChain.id) {
			await switchChainAsync({ chainId: targetChain.id })
		}
	}, [adminAddress, chainId, connectWallet, currency, isConnected, switchChainAsync, targetChain])

	const payWithMetaMask = useCallback(async () => {
		if (!currency || !adminAddress || !amount) {
			return
		}

		setLastError(null)

		try {
			await ensureWalletReady()

			if (!address) {
				throw new Error('Wallet is not connected.')
			}

			if (!targetChain) {
				throw new Error(`Unsupported network: ${currency.network}`)
			}

			let transactionHash: string

			if (!isNativeTransfer) {
				const contractAddress = currency.contract_address?.trim()
				const isValidContract = Boolean(contractAddress && contractAddress.startsWith('0x') && contractAddress.length === 42)

				if (!isValidContract) {
					throw new Error(`${currency.currency_code} contract address is not configured for ${currency.network}.`)
				}

				const parsedAmount = parseUnits(amount, currency.decimals)

				if (parsedAmount > BigInt(Number.MAX_SAFE_INTEGER)) {
					throw new Error('Deposit amount is too large.')
				}

				transactionHash = await writeContractAsync({
					account: address,
					chain: targetChain,
					address: contractAddress as `0x${string}`,
					abi: erc20Abi,
					functionName: 'transfer',
					args: [adminAddress as `0x${string}`, parsedAmount],
				})
			} else {
				const parsedAmount = parseUnits(amount, currency.decimals)

				if (parsedAmount > BigInt(Number.MAX_SAFE_INTEGER)) {
					throw new Error('Deposit amount is too large.')
				}

				transactionHash = await sendTransactionAsync({
					to: adminAddress as `0x${string}`,
					value: parsedAmount,
					chainId: targetChain.id,
				})
			}

			if (!transactionHash?.trim()) {
				throw new Error('Transaction hash was not returned.')
			}

			try {
				await depositMetaMask({
					currency_code: currency.currency_code,
					amount,
					transaction_id: transactionHash,
					address: adminAddress,
					from: address,
				}).unwrap()
			} catch {
				setLastError('Unable to record MetaMask deposit.')
				// Global API toasts are handled by playerBaseQuery.
			}
		} catch (error) {
			const message = error instanceof Error ? error.message : 'Unable to submit MetaMask transaction.'
			setLastError(message)
			showApiErrorToast(message)
		}
	}, [address, adminAddress, amount, currency, depositMetaMask, ensureWalletReady, isNativeTransfer, sendTransactionAsync, targetChain, writeContractAsync])

	return {
		walletAddress: address,
		isConnected,
		isWrongChain,
		targetChain,
		isSubmitting,
		lastError,
		payWithMetaMask,
	}
}
